import { Button } from '../ui/button';
import { Save, FolderOpen, Trash2, BookmarkPlus, ArrowLeft, Download, Printer, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

function DropdownMenu({ trigger, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div className="absolute top-full right-0 mt-1 min-w-[180px] bg-white border border-[#e5e4df] rounded shadow-lg py-1 z-50">
          {items.map((item, i) => (
            <button key={i} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#2c2c2c] hover:bg-[#f3f2ef] text-left"
              onClick={() => { setOpen(false); item.onClick?.(); }}>
              {item.icon && <item.icon className="w-4 h-4 text-[#6b7280]" />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Toolbar({
  onSave,
  onSaveDraft,
  onLoadDraft,
  onClearDraft,
  isSaving,
  quoteId,
  lastAutoSaved,
  onPdfPreview,
  onPdfDownload,
  onPrint,
  backTo = '/',
  backLabel = 'Quotes',
}) {
  const navigate = useNavigate();

  return (
    <div
      className="toolbar sticky top-0 z-40 px-6 py-3 flex items-center justify-between shadow-sm no-print"
      style={{
        background: 'rgba(248, 247, 244, 0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(201, 168, 76, 0.2)',
      }}
      data-testid="toolbar"
    >
      <div className="flex items-center gap-1 sm:gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(backTo)}
          data-testid="toolbar-back-btn"
          className="text-[#1a1a2e] hover:bg-[#1a1a2e]/10"
        >
          <ArrowLeft className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{backLabel}</span>
        </Button>
        <div className="w-px h-6 bg-[#c9a84c]/30" />
        <Button
          variant="ghost"
          size="sm"
          onClick={onSaveDraft}
          data-testid="toolbar-save-draft-btn"
          className="text-[#2c2c2c] hover:bg-[#c9a84c]/10"
        >
          <BookmarkPlus className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Save Draft</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onLoadDraft}
          data-testid="toolbar-load-draft-btn"
          className="text-[#2c2c2c] hover:bg-[#c9a84c]/10"
        >
          <FolderOpen className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Load Draft</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearDraft}
          data-testid="toolbar-clear-draft-btn"
          className="text-[#2c2c2c] hover:bg-[#c9a84c]/10"
        >
          <Trash2 className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Clear Draft</span>
        </Button>
      </div>

      <div className="flex-1" />

      <DropdownMenu
        trigger={
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/10 rounded transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        }
        items={[
          { icon: FileText, label: 'PDF Preview', onClick: onPdfPreview },
          { icon: Download, label: 'Export PDF', onClick: onPdfDownload },
          { icon: Printer, label: 'Print', onClick: onPrint },
        ]}
      />

      <div className="flex items-center gap-2 sm:gap-3">
        {lastAutoSaved && (
          <span className="hidden sm:inline text-xs" style={{ color: '#9ca3af' }}>
            ● Auto-saved {lastAutoSaved}
          </span>
        )}
        <Button
          size="sm"
          onClick={onSave}
          disabled={isSaving}
          data-testid="toolbar-save-btn"
          className="bg-[#1a1a2e] text-white hover:bg-[#252545]"
        >
          <Save className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{isSaving ? 'Saving...' : quoteId ? 'Update' : 'Save'}</span>
        </Button>
      </div>
    </div>
  );
}
